// User mapping service - maps wallet addresses to user profiles
// Profile data is stored in IPFS, only metadata (username, walletAddress) is stored in Firestore
// IPFS hash is stored on Arc (Circle) blockchain, not in Firestore
import { db } from './firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, deleteField, collection, getDocs, query, where, serverTimestamp, runTransaction } from 'firebase/firestore';
import { fetchFromIPFS, decryptProfile } from './cryptoOperations';
import { getCIDFromArc } from './services/migrationService';
import { recordActivity, ACTIVITY_TYPES } from './services/activityService';

/**
 * Mapping of wallet addresses to usernames
 * This can be stored in Firestore or as a constant
 * For now, using a combination of both
 */
const WALLET_USER_MAP = {
  // Wallet mappings cleared - no users mapped
};

/**
 * Get user profile data structure
 */
export const getUserProfileData = (username) => {
  // All profiles cleared
  const profiles = {};

  return profiles[username] || null;
};

/**
 * Get username from wallet address
 */
export const getUsernameFromWallet = async (walletAddress) => {
  if (!walletAddress) return null;

  // Normalize address to lowercase
  const normalizedAddress = walletAddress.toLowerCase();

  // First check local mapping
  if (WALLET_USER_MAP[normalizedAddress]) {
    return WALLET_USER_MAP[normalizedAddress];
  }

  // Then check Firestore walletUsers collection for dynamic mappings
  try {
    const userMapRef = doc(db, 'walletUsers', normalizedAddress);
    const userMapSnap = await getDoc(userMapRef);
    
    if (userMapSnap.exists()) {
      const data = userMapSnap.data();
      if (data.username) {
        return data.username;
      }
    }
  } catch (error) {
    console.error('Error checking walletUsers collection for wallet mapping:', error);
  }

  // Fallback: Check profiles collection by walletAddress or _ownerAddress
  try {
    const profilesRef = collection(db, 'profiles');
    
    // Try walletAddress first (for published addresses)
    const walletAddressQuery = query(profilesRef, where('walletAddress', '==', normalizedAddress));
    const walletAddressSnapshot = await getDocs(walletAddressQuery);
    if (!walletAddressSnapshot.empty) {
      const profileDoc = walletAddressSnapshot.docs[0];
      const profileData = profileDoc.data();
      if (profileData.username) {
        return profileData.username;
      }
    }
    
    // If not found by walletAddress, try _ownerAddress (always present, even if walletAddress not published)
    const ownerAddressQuery = query(profilesRef, where('_ownerAddress', '==', normalizedAddress));
    const ownerAddressSnapshot = await getDocs(ownerAddressQuery);
    if (!ownerAddressSnapshot.empty) {
      const profileDoc = ownerAddressSnapshot.docs[0];
      const profileData = profileDoc.data();
      if (profileData.username) {
        return profileData.username;
      }
    }
  } catch (error) {
    console.error('Error checking profiles collection for wallet mapping:', error);
  }

  return null;
};

/**
 * Get wallet address from username
 * @param {string} username - The username
 * @returns {Promise<string|null>} The wallet address or null if not found
 */
export const getWalletFromUsername = async (username) => {
  if (!username) return null;

  try {
    // First check Firestore profiles collection (has walletAddress if published)
    const profileRef = doc(db, 'profiles', username);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const data = profileSnap.data();
      if (data.walletAddress) {
        console.log('Found wallet address in profiles collection (published):', data.walletAddress);
        return data.walletAddress;
      }
      // If walletAddress not published, check _ownerAddress (internal field, always stored)
      if (data._ownerAddress) {
        console.log('Found wallet address from _ownerAddress (unpublished):', data._ownerAddress);
        return data._ownerAddress;
      }
      console.log('Profile exists but no wallet address found, checking walletUsers...');
    } else {
      console.warn('Profile does not exist for username:', username);
    }
    
    // If not found in profiles, check walletUsers collection
    // Try querying walletUsers where username matches (requires index)
    try {
      const walletUsersRef = collection(db, 'walletUsers');
      const q = query(walletUsersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Get the first matching document - the document ID is the wallet address
        const docSnap = querySnapshot.docs[0];
        const walletAddr = docSnap.id;
        console.log('Found wallet address from walletUsers query:', walletAddr);
        return walletAddr;
      }
    } catch (queryError) {
      // Query might fail if index doesn't exist - try fallback method
      console.warn('Query by username failed, trying fallback method:', queryError);
      
      // Fallback: Get all walletUsers and filter client-side (less efficient but works without index)
      try {
        const walletUsersRef = collection(db, 'walletUsers');
        const allWalletUsersSnapshot = await getDocs(walletUsersRef);
        
        // Find document where username matches (case-insensitive)
        const usernameLower = username.toLowerCase();
        for (const docSnap of allWalletUsersSnapshot.docs) {
          const data = docSnap.data();
          const docUsername = data.username;
          if (docUsername && docUsername.toLowerCase() === usernameLower) {
            const walletAddr = docSnap.id;
            console.log('Found wallet address from walletUsers fallback:', walletAddr, 'for username:', username);
            return walletAddr;
          }
        }
        console.warn('Username not found in walletUsers collection:', username);
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
      }
    }
  } catch (error) {
    console.error('Error getting wallet from username:', error);
  }

  return null;
};

/**
 * Create or update wallet-to-user mapping in Firestore
 */
export const setWalletUserMapping = async (walletAddress, username) => {
  if (!walletAddress || !username) {
    throw new Error('Wallet address and username are required');
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const userMapRef = doc(db, 'walletUsers', normalizedAddress);
    
    await setDoc(userMapRef, {
      walletAddress: normalizedAddress,
      username: username,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });

    return true;
  } catch (error) {
    console.error('Error setting wallet-user mapping:', error);
    throw error;
  }
};

/**
 * Check if a wallet address is mapped to a user
 */
export const isWalletMapped = async (walletAddress) => {
  const username = await getUsernameFromWallet(walletAddress);
  return username !== null;
};

/**
 * Check if a username is already taken by another wallet
 * @param {string} username - The username to check
 * @param {string} excludeWalletAddress - Optional wallet address to exclude from check (for updates)
 * @returns {Promise<boolean>} True if username is taken by another wallet, false otherwise
 */
export const isUsernameTaken = async (username, excludeWalletAddress = null) => {
  if (!username) return false;

  try {
    const profileRef = doc(db, 'profiles', username);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return false; // Username doesn't exist
    }
    
    const profileData = profileSnap.data();
    const existingWalletAddress = profileData.walletAddress;
    
    // If no excludeWalletAddress provided, username is taken
    if (!excludeWalletAddress) {
      return true;
    }
    
    // If excludeWalletAddress matches, username is not taken (same user updating)
    const normalizedExclude = excludeWalletAddress.toLowerCase();
    const normalizedExisting = existingWalletAddress ? existingWalletAddress.toLowerCase() : null;
    
    return normalizedExisting !== normalizedExclude;
  } catch (error) {
    console.error('Error checking if username is taken:', error);
    // On error, assume username is not taken to avoid blocking registration
    return false;
  }
};

/**
 * Atomically get the next sequential profile ID
 */
export const getNextProfileId = async () => {
  const counterRef = doc(db, 'counters', 'profile_id');
  const nextId = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const current = snap.exists() ? (snap.data().next || 1) : 1;
    const assigned = current;
    transaction.set(counterRef, { next: current + 1 }, { merge: true });
    return assigned;
  });
  return nextId;
};

/**
 * Note: CID is retrieved from Arc (Circle) blockchain using getCIDFromArc
 * The old getCIDFromBlockchain (from @peopletag/protocol) reads from Arbitrum
 * and is no longer used since all profiles are on Arc (Circle) network
 */

/**
 * Save user profile metadata to Firestore (actual profile data is stored in IPFS)
 * Only stores: username, walletAddress, avatar (IPFS hash is stored on blockchain, not Firestore)
 * @param {string} walletAddress - The wallet address
 * @param {object} profileData - The profile data (no ipfsHash needed - stored on blockchain)
 * @returns {Promise<boolean>}
 */
/**
 * Update only published metadata in Firestore (without IPFS update)
 * Published elements are stored in main fields (tagsList, socialLinks, etc.)
 * Unpublished elements are removed from Firestore (set to null/empty)
 */
export const updatePublishedMetadata = async (walletAddress, publishedData) => {
  if (!walletAddress || !publishedData || !publishedData.username) {
    throw new Error('Wallet address, username, and published data are required');
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const profileRef = doc(db, 'profiles', publishedData.username);
    
    // Get existing document to preserve other fields
    const existingDoc = await getDoc(profileRef);
    if (!existingDoc.exists()) {
      throw new Error('Profile not found');
    }
    const existingData = existingDoc.data();
    
    // Update only published elements in main fields (no published* fields)
    // Unpublished elements are set to null/empty arrays
    // For wallet: if published, walletAddress is stored in profiles; if unpublished, it's removed (but remains in walletUsers for mapping)
    // For name: if published, name is stored in profiles; if unpublished, it's removed
    // For bio: if published, bio is stored in profiles; if unpublished, it's removed
    const updateData = {
      // _ownerAddress is an internal field that is NEVER deleted - used for ownership verification in Firestore rules
      // This allows walletAddress to be unpublished while still maintaining ownership
      _ownerAddress: normalizedAddress,
      // Always include username for Firestore rules validation
      username: publishedData.username,
      // Only published tags are stored in tagsList
      tagsList: Array.isArray(publishedData.publishedTags) ? publishedData.publishedTags : [],
      // Only published groups are stored in groups
      groups: Array.isArray(publishedData.publishedGroups) ? publishedData.publishedGroups : [],
      // Only published socials are stored in socialLinks
      socialLinks: Array.isArray(publishedData.publishedSocials) ? publishedData.publishedSocials : [],
      // Only published music is stored in musicList
      musicList: Array.isArray(publishedData.publishedMusic) ? publishedData.publishedMusic : [],
      // Only published birthDate is stored
      birthDate: publishedData.publishedBirthDate || null,
      age: publishedData.publishedAge != null ? publishedData.publishedAge : null,
      // Only published city is stored
      city: publishedData.publishedCity || null,
      location: publishedData.publishedLocation || null,
      updatedAt: serverTimestamp(),
    };
    
    // Name: if published, include name in profiles; if unpublished, remove it
    if (publishedData.publishedName && publishedData.publishedName.trim() !== '') {
      updateData.name = publishedData.publishedName;
    } else {
      // Remove name from profiles (unpublished)
      updateData.name = deleteField();
    }
    
    // Bio: if published, include bio in profiles; if unpublished, remove it
    if (publishedData.publishedBio && publishedData.publishedBio.trim() !== '') {
      updateData.bio = publishedData.publishedBio;
      console.log('📝 Bio will be SET in Firestore:', publishedData.publishedBio.substring(0, 50) + '...');
    } else {
      // Remove bio from profiles (unpublished)
      updateData.bio = deleteField();
      console.log('🗑️ Bio will be DELETED from Firestore (unpublished)');
    }
    
    // Wallet: if published, include walletAddress in profiles; if unpublished, remove it
    // Note: walletAddress always remains in walletUsers collection for mapping
    // Always use updateDoc to ensure deleteField() operations work correctly
    if (publishedData.publishedWallet) {
      updateData.walletAddress = normalizedAddress;
    } else {
      // Remove walletAddress from profiles (unpublished), but keep it in walletUsers for mapping
      updateData.walletAddress = deleteField();
    }
    
    // Email: if published, include email in profiles; if unpublished, remove it
    if (publishedData.publishedEmail && publishedData.publishedEmail.trim() !== '') {
      updateData.email = publishedData.publishedEmail;
      console.log('📧 Email will be SET in Firestore:', publishedData.publishedEmail);
    } else {
      // Remove email from profiles (unpublished)
      updateData.email = deleteField();
      console.log('🗑️ Email will be DELETED from Firestore (unpublished)');
    }
    
    // Preferred Contact: if published, include preferredContactMethod in profiles; if unpublished, remove it
    if (publishedData.publishedPreferredContact && publishedData.publishedPreferredContact.trim() !== '') {
      updateData.preferredContactMethod = publishedData.publishedPreferredContact;
      console.log('📞 Preferred Contact will be SET in Firestore:', publishedData.publishedPreferredContact);
    } else {
      // Remove preferredContactMethod from profiles (unpublished)
      updateData.preferredContactMethod = deleteField();
      console.log('🗑️ Preferred Contact will be DELETED from Firestore (unpublished)');
    }
    
    // Relationship: if published, include relationshipStatus in profiles; if unpublished, remove it
    if (publishedData.publishedRelationship && publishedData.publishedRelationship.trim() !== '') {
      updateData.relationshipStatus = publishedData.publishedRelationship;
      console.log('💑 Relationship will be SET in Firestore:', publishedData.publishedRelationship);
    } else {
      updateData.relationshipStatus = deleteField();
      console.log('🗑️ Relationship will be DELETED from Firestore (unpublished)');
    }
    
    // Looking For: if published, include lookingFor in profiles; if unpublished, remove it
    if (publishedData.publishedLookingFor && publishedData.publishedLookingFor.trim() !== '') {
      updateData.lookingFor = publishedData.publishedLookingFor;
      console.log('👀 Looking For will be SET in Firestore:', publishedData.publishedLookingFor);
    } else {
      updateData.lookingFor = deleteField();
      console.log('🗑️ Looking For will be DELETED from Firestore (unpublished)');
    }
    
    // Languages: if published, include languages in profiles; if unpublished, remove it
    if (publishedData.publishedLanguages && publishedData.publishedLanguages.trim() !== '') {
      updateData.languages = publishedData.publishedLanguages;
      console.log('🗣️ Languages will be SET in Firestore:', publishedData.publishedLanguages);
    } else {
      updateData.languages = deleteField();
      console.log('🗑️ Languages will be DELETED from Firestore (unpublished)');
    }
    
    // Education: if published, include education in profiles; if unpublished, remove it
    if (publishedData.publishedEducation && publishedData.publishedEducation.trim() !== '') {
      updateData.education = publishedData.publishedEducation;
      console.log('🎓 Education will be SET in Firestore:', publishedData.publishedEducation);
    } else {
      updateData.education = deleteField();
      console.log('🗑️ Education will be DELETED from Firestore (unpublished)');
    }
    
    // Occupation: if published, include occupation in profiles; if unpublished, remove it
    if (publishedData.publishedOccupation && publishedData.publishedOccupation.trim() !== '') {
      updateData.occupation = publishedData.publishedOccupation;
      console.log('💼 Occupation will be SET in Firestore:', publishedData.publishedOccupation);
    } else {
      updateData.occupation = deleteField();
      console.log('🗑️ Occupation will be DELETED from Firestore (unpublished)');
    }
    
    // Job Title (role): if published, include role in profiles; if unpublished, remove it
    if (publishedData.publishedJobTitle && publishedData.publishedJobTitle.trim() !== '') {
      updateData.role = publishedData.publishedJobTitle;
      console.log('💼 Job Title will be SET in Firestore:', publishedData.publishedJobTitle);
    } else {
      updateData.role = deleteField();
      console.log('🗑️ Job Title will be DELETED from Firestore (unpublished)');
    }
    
    // Use updateDoc to ensure deleteField() operations work correctly
    console.log('💾 Updating Firestore with:', {
      hasBio: 'bio' in updateData,
      bioIsDeleteField: updateData.bio && updateData.bio.constructor && updateData.bio.constructor.name === 'FieldValue',
      username: publishedData.username
    });
    await updateDoc(profileRef, updateData);

    // Record activity for profile update
    try {
      await recordActivity(ACTIVITY_TYPES.PROFILE_UPDATE, normalizedAddress, {
        username: publishedData.username
      });
    } catch (error) {
      console.warn('Error recording profile update activity:', error);
    }
    
    // Verify the update worked by reading back the document
    const verifyDoc = await getDoc(profileRef);
    if (verifyDoc.exists()) {
      const verifyData = verifyDoc.data();
      const bioExists = verifyData.bio !== undefined;
      console.log('✅ Published metadata updated in Firestore. Verification:', {
        bioExists,
        bioValue: bioExists ? verifyData.bio : 'DELETED',
        expectedDeleted: !(publishedData.publishedBio && publishedData.publishedBio.trim() !== '')
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating published metadata:', error);
    throw error;
  }
};

/**
 * Update lastvisit timestamp for a user
 * @param {string} walletAddress - The wallet address
 * @returns {Promise<boolean>} True if successful, false if user not found
 */
export const updateLastVisit = async (walletAddress) => {
  if (!walletAddress) {
    return false;
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    
    // Get username from wallet address
    const username = await getUsernameFromWallet(normalizedAddress);
    if (!username) {
      console.log('No username found for wallet address, skipping lastvisit update');
      return false;
    }

    // Update lastvisit timestamp in the profile document
    const profileRef = doc(db, 'profiles', username);
    await updateDoc(profileRef, {
      // Required for Firestore rules validation
      _ownerAddress: normalizedAddress,
      username: username,
      lastvisit: serverTimestamp()
    });

    console.log('✅ Lastvisit timestamp updated for user:', username);
    return true;
  } catch (error) {
    console.error('Error updating lastvisit timestamp:', error);
    // Don't throw - this is a non-critical operation
    return false;
  }
};

export const saveUserProfile = async (walletAddress, profileData) => {
  if (!walletAddress || !profileData || !profileData.username) {
    throw new Error('Wallet address, username, and profile data are required');
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    
    // Log profile save for debugging
    console.log('Saving profile metadata to Firestore (IPFS hash stored on blockchain):', {
      username: profileData.username
    });
    
    // Save only metadata to Firestore (profiles collection)
    // Profile data itself is stored in IPFS, IPFS hash is stored on blockchain (not in Firestore)
    const profileRef = doc(db, 'profiles', profileData.username);
    try {
      // First, get existing document to preserve createdAt if it exists
      const existingDoc = await getDoc(profileRef);
      const existingData = existingDoc.exists() ? existingDoc.data() : null;
      const isNewUser = !existingDoc.exists();

      // Assign sequential user_id if missing
      const userIdValue = existingData?.user_id != null ? existingData.user_id : await getNextProfileId();
      
      // Explicitly set city and location to null if not provided (to override existing values)
      const cityValue = profileData.city != null ? profileData.city : null;
      const locationValue = (profileData.location && profileData.location.lat != null && profileData.location.lng != null)
          ? {
              lat: profileData.location.lat,
              lng: profileData.location.lng,
              city: profileData.location.city || profileData.city || null
            }
          : null;
      // Explicitly set name to null if not provided (to override existing values)
      const nameValue = profileData.name != null ? profileData.name : null;
      // Explicitly set bio to null if not provided (to override existing values)
      const bioValue = profileData.bio != null ? profileData.bio : null;
      // Explicitly set email to null if not provided (to override existing values)
      const emailValue = profileData.email != null ? profileData.email : (existingData?.email || null);
      
      // Get photos array (with backward compatibility)
      const photos = Array.isArray(profileData.photos) ? profileData.photos : 
                     (profileData.avatar ? [profileData.avatar] : []);
      const avatar = photos.length > 0 ? photos[0] : (profileData.avatar || null);
      
      await setDoc(profileRef, {
        username: profileData.username,
        walletAddress: normalizedAddress,
        // _ownerAddress is an internal field that is NEVER deleted - used for ownership verification
        _ownerAddress: normalizedAddress,
        user_id: userIdValue,
        // ipfsHash is stored in Firestore as fallback (primary source is Arc (Circle) blockchain)
        ipfsHash: profileData.ipfsHash || null, // Store hash in Firestore as fallback
        avatar: avatar, // Keep for backward compatibility
        photos: photos, // New photos array
        name: nameValue,
        bio: bioValue,
        email: emailValue,
        city: cityValue,
        location: locationValue,
        // Only published elements are stored in Firestore (visible to others)
        // Unpublished elements are not stored here - they're only in IPFS
        tagsList: Array.isArray(profileData.tagsList) ? profileData.tagsList : (existingData?.tagsList || []),
        socialLinks: Array.isArray(profileData.socialLinks) ? profileData.socialLinks : (existingData?.socialLinks || []),
        groups: Array.isArray(profileData.groups) ? profileData.groups : (existingData?.groups || []),
        birthDate: profileData.birthDate || existingData?.birthDate || null,
        age: profileData.age != null ? profileData.age : (existingData?.age != null ? existingData.age : null),
        updatedAt: serverTimestamp(),
        createdAt: existingData?.createdAt || serverTimestamp(),
      }, { merge: true });

      // Record activity
      if (isNewUser) {
        // New user joined
        try {
          await recordActivity(ACTIVITY_TYPES.USER_JOINED, normalizedAddress, {
            username: profileData.username,
            location: locationValue,
            city: cityValue
          });
        } catch (error) {
          console.warn('Error recording user joined activity:', error);
        }
      } else {
        // Existing user updated their profile (e.g. bio, tags, socials, etc.)
        try {
          await recordActivity(ACTIVITY_TYPES.PROFILE_UPDATE, normalizedAddress, {
            username: profileData.username
          });
        } catch (error) {
          console.warn('Error recording profile update activity:', error);
        }
      }
    } catch (setDocError) {
      // Check if error is due to browser extension blocking (ERR_BLOCKED_BY_CLIENT)
      // This is usually non-critical - data might still be saved
      if (setDocError.message && setDocError.message.includes('ERR_BLOCKED_BY_CLIENT')) {
        console.warn('⚠️ Firestore request blocked by browser extension. Data may still be saved. Consider disabling ad blocker for this site.');
        // Try to verify if data was saved by reading it back
        try {
          const savedDoc = await getDoc(profileRef);
          if (savedDoc.exists()) {
            console.log('✅ Profile metadata verified - saved successfully despite blocking warning');
          } else {
            throw new Error('Profile metadata was not saved - blocked by browser extension');
          }
        } catch (verifyError) {
          throw new Error('Failed to save profile metadata - blocked by browser extension. Please disable ad blocker for this site.');
        }
      } else {
        throw setDocError;
      }
    }

    // Save wallet-to-username mapping FIRST (before verification)
    // This ensures that getUsernameFromWallet can find the username immediately
    await setWalletUserMapping(normalizedAddress, profileData.username);
    console.log('✅ Wallet-to-username mapping saved:', normalizedAddress, '->', profileData.username);

    // Verify that profile was saved correctly by reading it back
    let verificationAttempts = 0;
    const maxAttempts = 10; // Increased attempts for better reliability
    while (verificationAttempts < maxAttempts) {
      const verifyDoc = await getDoc(profileRef);
      if (verifyDoc.exists()) {
        const savedData = verifyDoc.data();
        if (savedData.username === profileData.username) {
          console.log('✅ Profile metadata verified - username matches:', profileData.username);
          // Also verify wallet mapping exists
          const walletMapRef = doc(db, 'walletUsers', normalizedAddress);
          const walletMapDoc = await getDoc(walletMapRef);
          if (walletMapDoc.exists()) {
            console.log('✅ Wallet-to-username mapping verified');
          } else {
            console.warn('⚠️ Wallet-to-username mapping not found, recreating...');
            await setWalletUserMapping(normalizedAddress, profileData.username);
          }
          return true;
        } else {
          console.log(`⚠️ Username mismatch. Expected: ${profileData.username}, Got: ${savedData.username || 'null'}. Retrying... (attempt ${verificationAttempts + 1}/${maxAttempts})`);
        }
      } else {
        console.log(`⚠️ Profile document does not exist. Retrying... (attempt ${verificationAttempts + 1}/${maxAttempts})`);
      }
      verificationAttempts++;
      if (verificationAttempts < maxAttempts) {
        // Wait 300ms before retrying (increased delay)
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    // If verification failed, still return true but log warning
    console.warn('⚠️ Could not verify profile was saved correctly after', maxAttempts, 'attempts');
    console.log('Profile metadata saved to Firestore (verification incomplete)');
    
    // Final check - try to read the document one more time
    const finalCheck = await getDoc(profileRef);
    if (finalCheck.exists()) {
      const finalData = finalCheck.data();
      console.log('📋 Final check - Document exists:', {
        username: finalData.username
      });
    } else {
      console.error('❌ Final check - Document does NOT exist!');
    }
    
    return true;
  } catch (error) {
    console.error('Error saving user profile metadata:', error);
    throw error;
  }
};

/**
 * Get user profile by wallet address
 * Profile data is loaded from IPFS, IPFS hash is read from Arc (Circle) blockchain
 * @param {string} walletAddress - The wallet address
 * @param {Uint8Array} encryptionKey - Optional encryption key to decrypt profile data from IPFS
 * @returns {Promise<object|null>} The profile data or null if not found
 */
export const getUserProfileByWallet = async (walletAddress, encryptionKey = null) => {
  if (!walletAddress) return null;

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    
    // Add timeout for username lookup (reduced to 1s for faster loading)
    const usernameTimeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Username lookup timeout')), 1000);
    });
    
    // First get username from wallet mapping with timeout
    let username = await Promise.race([
      getUsernameFromWallet(walletAddress),
      usernameTimeout
    ]).catch(err => {
      if (err.message === 'Username lookup timeout') {
        console.log('Username lookup timed out, trying alternative method...');
        return null;
      }
      throw err;
    });
    
    // If username not found via mapping, try to find profile by walletAddress or _ownerAddress directly
    // This is a fallback for cases where mapping might not be saved yet
    // IMPORTANT: Also try _ownerAddress because walletAddress may be unpublished (deleted from profiles)
    // but _ownerAddress is NEVER deleted - it's an internal ownership field
    // Add timeout to prevent blocking (1.5s max for both queries)
    if (!username) {
      console.log('Username not found via mapping, searching profiles by walletAddress...');
      try {
        const profilesRef = collection(db, 'profiles');
        const searchTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Profile search timeout')), 1500);
        });
        
        // First try by walletAddress (if it's published)
        let q = query(profilesRef, where('walletAddress', '==', normalizedAddress));
        let querySnapshot = await Promise.race([
          getDocs(q),
          searchTimeout
        ]).catch(err => {
          if (err.message === 'Profile search timeout') {
            console.log('Profile search by walletAddress timed out');
            return null;
          }
          throw err;
        });
        
        if (querySnapshot && !querySnapshot.empty) {
          const profileDoc = querySnapshot.docs[0];
          const profileData = profileDoc.data();
          username = profileData.username;
          console.log('✅ Found profile by walletAddress, username:', username);
        } else if (querySnapshot) {
          // walletAddress not found - try _ownerAddress (never deleted, used for ownership)
          console.log('No profile found by walletAddress (may be unpublished), trying _ownerAddress...');
          q = query(profilesRef, where('_ownerAddress', '==', normalizedAddress));
          querySnapshot = await Promise.race([
            getDocs(q),
            searchTimeout
          ]).catch(err => {
            if (err.message === 'Profile search timeout') {
              console.log('Profile search by _ownerAddress timed out');
              return null;
            }
            throw err;
          });
          
          if (querySnapshot && !querySnapshot.empty) {
            const profileDoc = querySnapshot.docs[0];
            const profileData = profileDoc.data();
            username = profileData.username;
            console.log('✅ Found profile by _ownerAddress, username:', username);
          } else {
            console.log('No profile found by walletAddress or _ownerAddress');
            return null;
          }
        } else {
          console.log('Profile search timed out');
          return null;
        }
      } catch (searchError) {
        console.error('Error searching profiles by walletAddress/_ownerAddress:', searchError);
        return null;
      }
    }
    
    if (!username) return null;

    // Add timeout for profile metadata lookup (reduced to 1s for faster loading)
    const profileTimeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Profile metadata lookup timeout')), 1000);
    });

    // Get profile metadata from Firestore (does NOT contain ipfsHash - it's on blockchain)
    const profileSnap = await Promise.race([
      getDoc(doc(db, 'profiles', username)),
      profileTimeout
    ]).catch(err => {
      if (err.message === 'Profile metadata lookup timeout') {
        console.log('Profile metadata lookup timed out');
        return null;
      }
      throw err;
    });
    
    if (profileSnap && profileSnap.exists()) {
      const metadata = profileSnap.data();
      
      // Get IPFS hash from Arc (Circle) blockchain with timeout (parallel with metadata load)
      // Fallback to Firestore hash if blockchain query fails (for reliability)
      let ipfsHash = null;
      try {
        // Add timeout to blockchain query (3s max) to prevent blocking
        const blockchainTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Blockchain query timeout')), 3000);
        });
        
        // Use getCIDFromArc instead of getCIDFromBlockchain since we're saving to Arc (Circle) network
        ipfsHash = await Promise.race([
          getCIDFromArc(normalizedAddress),
          blockchainTimeout
        ]).catch(err => {
          if (err.message === 'Blockchain query timeout') {
            console.log('⚠️ Blockchain CID query timed out, using Firestore hash as fallback');
            return null;
          }
          throw err;
        });
        
        if (!ipfsHash && metadata.ipfsHash) {
          // Blockchain query failed/returned null, use Firestore hash as fallback
          console.log('⚠️ Blockchain CID query returned null, using Firestore hash as fallback:', metadata.ipfsHash);
          ipfsHash = metadata.ipfsHash;
        }
      } catch (blockchainError) {
        console.warn('⚠️ Error querying blockchain for CID, using Firestore hash as fallback:', blockchainError);
        // If blockchain query fails, use Firestore hash if available
        if (metadata.ipfsHash) {
          ipfsHash = metadata.ipfsHash;
          console.log('✅ Using IPFS hash from Firestore fallback:', ipfsHash);
        }
      }
      
        // If no encryption key provided, return only metadata (with IPFS hash from blockchain)
        if (!encryptionKey || !ipfsHash) {
          console.log('Profile metadata loaded from Firestore (no IPFS data):', {
            username: metadata.username,
            ipfsHash: ipfsHash || 'not found on blockchain'
          });
          // Get photos from metadata (with backward compatibility)
          const photos = Array.isArray(metadata.photos) ? metadata.photos : 
                         (metadata.avatar ? [metadata.avatar] : []);
          // Track if walletAddress was actually in Firestore (published) vs using fallback
          const isWalletPublishedInFirestore = !!(metadata.walletAddress && metadata.walletAddress.toLowerCase() === normalizedAddress);
          return {
            ...metadata,
            walletAddress: metadata.walletAddress || normalizedAddress, // Always include wallet address even if not published
            _isWalletPublishedInFirestore: isWalletPublishedInFirestore, // Explicit flag for publish status check
            ipfsHash: ipfsHash || null,
            photos: photos // Include photos array
          };
        }

      // Load encrypted profile data from IPFS using hash from blockchain
      try {
        console.log('Loading profile data from IPFS (hash from blockchain):', ipfsHash);
        const encryptedData = await fetchFromIPFS(ipfsHash);
        
        // Decrypt profile data
        const decryptedProfile = decryptProfile(encryptedData, encryptionKey);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'userMappingService.js:841',message:'After decryptProfile',data:{hasBio:decryptedProfile.bio!==undefined,bioValue:decryptedProfile.bio,bioLength:decryptedProfile.bio?.length,ipfsHash:ipfsHash},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        
        // Debug: Log decrypted profile data to verify all fields are present
        console.log('🔓 Decrypted profile data:', {
          hasTagsList: Array.isArray(decryptedProfile.tagsList),
          tagsListLength: decryptedProfile.tagsList?.length || 0,
          hasSocialLinks: Array.isArray(decryptedProfile.socialLinks),
          socialLinksLength: decryptedProfile.socialLinks?.length || 0,
          hasMusicList: Array.isArray(decryptedProfile.musicList),
          musicListLength: decryptedProfile.musicList?.length || 0,
          hasBirthDate: !!decryptedProfile.birthDate,
          birthDate: decryptedProfile.birthDate,
          hasEmail: !!decryptedProfile.email,
          email: decryptedProfile.email ? decryptedProfile.email.substring(0, 10) + '...' : null,
          hasCity: !!decryptedProfile.city,
          city: decryptedProfile.city,
          hasName: !!decryptedProfile.name,
          name: decryptedProfile.name,
          hasBio: decryptedProfile.bio !== undefined,
          bioLength: decryptedProfile.bio?.length || 0,
          bioPreview: decryptedProfile.bio ? (decryptedProfile.bio.substring(0, 50) + (decryptedProfile.bio.length > 50 ? '...' : '')) : null
        });
        
        // Merge metadata with decrypted profile data
        // IMPORTANT: All profile data (tagsList, socialLinks, bio, name, birthDate, city, etc.) should come from IPFS (decryptedProfile)
        // because IPFS contains ALL data (both published and unpublished)
        // Firestore only contains published metadata, so we use IPFS data and determine published status by comparing with Firestore
        // Photos are stored in Firestore (not encrypted), so use Firestore photos, fallback to IPFS/avatar
        const photos = Array.isArray(metadata.photos) ? metadata.photos : 
                       (Array.isArray(decryptedProfile.photos) ? decryptedProfile.photos : 
                        (metadata.avatar ? [metadata.avatar] : 
                         (decryptedProfile.avatar ? [decryptedProfile.avatar] : [])));
        const avatar = photos.length > 0 ? photos[0] : (metadata.avatar || decryptedProfile.avatar || null);
        
        // Track if walletAddress was actually in Firestore (published) vs using fallback
        const isWalletPublishedInFirestore = !!(metadata.walletAddress && metadata.walletAddress.toLowerCase() === normalizedAddress);
        
        // IMPORTANT: Start with decryptedProfile to preserve ALL fields from IPFS
        // Only override specific fields that need to come from metadata
        const fullProfile = {
          ...decryptedProfile, // Start with ALL data from IPFS (tagsList, socialLinks, musicList, birthDate, email, city, etc.)
          // Override with metadata fields that are always public (username, walletAddress, ipfsHash, avatar, photos)
          username: metadata.username,
          walletAddress: metadata.walletAddress || normalizedAddress, // Use metadata if published, otherwise use the address we loaded with
          _isWalletPublishedInFirestore: isWalletPublishedInFirestore, // Explicit flag for publish status check
          _isTestRecord: metadata._isTestRecord === true ? true : false, // Preserve test user flag from Firestore metadata
          ipfsHash: ipfsHash, // From blockchain, not Firestore
          avatar: avatar, // Use first photo or fallback to avatar
          photos: photos, // Photos from Firestore (not encrypted)
          // Ensure arrays exist (fallback to empty array if somehow missing)
          tagsList: Array.isArray(decryptedProfile.tagsList) ? decryptedProfile.tagsList : (metadata.tagsList || []),
          socialLinks: Array.isArray(decryptedProfile.socialLinks) ? decryptedProfile.socialLinks : (metadata.socialLinks || []),
          musicList: Array.isArray(decryptedProfile.musicList) ? decryptedProfile.musicList : (metadata.musicList || []),
          groups: Array.isArray(decryptedProfile.groups) ? decryptedProfile.groups : (metadata.groups || []),
          // Preserve all other fields from decryptedProfile (name, birthDate, age, email, city, location, bio, etc.)
          // These are already included in the spread above, but we ensure they're not null if they exist
          // IMPORTANT: Prefer IPFS data (decryptedProfile) over Firestore (metadata) because IPFS has ALL data
          // Firestore only has published fields, so if a field exists in IPFS, use it even if it's an empty string
          name: decryptedProfile.name !== undefined ? decryptedProfile.name : (metadata.name || null),
          city: decryptedProfile.city !== undefined ? decryptedProfile.city : (metadata.city || null),
          location: decryptedProfile.location !== undefined ? decryptedProfile.location : (metadata.location || null),
          birthDate: decryptedProfile.birthDate !== undefined ? decryptedProfile.birthDate : (metadata.birthDate || null),
          age: decryptedProfile.age != null ? decryptedProfile.age : (metadata.age != null ? metadata.age : null),
          email: decryptedProfile.email !== undefined ? decryptedProfile.email : (metadata.email || null),
          bio: decryptedProfile.bio !== undefined ? decryptedProfile.bio : (metadata.bio || null),
          updatedAt: metadata.updatedAt,
          createdAt: metadata.createdAt
        };
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'userMappingService.js:907',message:'fullProfile after merge',data:{fullProfileBio:fullProfile.bio,fullProfileBioLength:fullProfile.bio?.length,decryptedBio:decryptedProfile.bio,metadataBio:metadata.bio,hasDecryptedBio:decryptedProfile.bio!==undefined,hasMetadataBio:metadata.bio!==undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion
        
        console.log('✅ Profile loaded from IPFS and decrypted successfully', {
          finalTagsListLength: fullProfile.tagsList?.length || 0,
          finalMusicListLength: fullProfile.musicList?.length || 0,
          finalSocialLinksLength: fullProfile.socialLinks?.length || 0,
          hasBirthDate: !!fullProfile.birthDate,
          hasEmail: !!fullProfile.email,
          hasBio: !!fullProfile.bio,
          bioLength: fullProfile.bio?.length || 0,
          bioFromIPFS: decryptedProfile.bio !== undefined,
          bioFromFirestore: metadata.bio !== undefined
        });
        return fullProfile;
      } catch (ipfsError) {
        console.error('Error loading profile from IPFS:', ipfsError);
        // Return metadata only if IPFS load fails (with IPFS hash from blockchain)
        // Get photos from metadata (with backward compatibility)
        const photos = Array.isArray(metadata.photos) ? metadata.photos : 
                       (metadata.avatar ? [metadata.avatar] : []);
        // Track if walletAddress was actually in Firestore (published)
        const isWalletPublishedInFirestore = !!(metadata.walletAddress && metadata.walletAddress.toLowerCase() === normalizedAddress);
        return {
          ...metadata,
          _isWalletPublishedInFirestore: isWalletPublishedInFirestore, // Explicit flag for publish status check
          ipfsHash: ipfsHash || null,
          photos: photos // Include photos array
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user profile by wallet:', error);
    return null;
  }
};

/**
 * Update only photos and avatar in Firestore without affecting other fields
 * This is used when uploading/deleting photos to preserve published state of other fields
 * @param {string} walletAddress - The wallet address
 * @param {string} username - The username
 * @param {Array} photos - Array of photo base64 strings
 * @param {string|null} avatar - Avatar base64 string (first photo or null)
 * @returns {Promise<boolean>}
 */
export const updateUserPhotos = async (walletAddress, username, photos, avatar) => {
  if (!walletAddress || !username) {
    throw new Error('Wallet address and username are required');
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const profileRef = doc(db, 'profiles', username);
    
    // Use updateDoc to only update photos and avatar fields, preserving all other fields
    await updateDoc(profileRef, {
      // Required for Firestore rules validation
      _ownerAddress: normalizedAddress,
      username: username,
      photos: Array.isArray(photos) ? photos : [],
      avatar: avatar || null,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Photos updated in Firestore (other fields preserved)');
    return true;
  } catch (error) {
    console.error('Error updating user photos:', error);
    throw error;
  }
};

/**
 * Delete user profile and all associated data from Firestore
 * @param {string} walletAddress - The wallet address
 * @returns {Promise<boolean>}
 */
export const deleteUserProfile = async (walletAddress) => {
  if (!walletAddress) {
    throw new Error('Wallet address is required');
  }

  try {
    const normalizedAddress = walletAddress.toLowerCase();
    console.log('🗑️ Starting profile deletion for wallet:', normalizedAddress);
    
    // First try to get username from wallet mapping
    let username = await getUsernameFromWallet(normalizedAddress);
    console.log('🗑️ Username from wallet mapping:', username);
    
    // If no username found via mapping, search profiles collection directly
    // This handles cases where wallet mapping was deleted but profile still exists
    if (!username) {
      console.log('🔍 Searching profiles by walletAddress or _ownerAddress...');
      try {
        const profilesRef = collection(db, 'profiles');
        
        // Try walletAddress first
        let q = query(profilesRef, where('walletAddress', '==', normalizedAddress));
        let querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          username = querySnapshot.docs[0].data().username;
          console.log('✅ Found profile by walletAddress, username:', username);
        } else {
          // Try _ownerAddress
          q = query(profilesRef, where('_ownerAddress', '==', normalizedAddress));
          querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            username = querySnapshot.docs[0].data().username;
            console.log('✅ Found profile by _ownerAddress, username:', username);
          }
        }
      } catch (searchError) {
        console.warn('⚠️ Error searching profiles:', searchError);
      }
    }
    
    if (username) {
      // Delete profile from Firestore (profiles collection)
      try {
        const profileRef = doc(db, 'profiles', username);
        console.log('🗑️ Deleting profile document:', username);
        await deleteDoc(profileRef);
        console.log('✅ Profile deleted:', username);
      } catch (profileError) {
        console.error('❌ Error deleting profile document:', profileError.code, profileError.message);
        // Continue to try deleting wallet mapping even if profile deletion fails
      }
    } else {
      console.log('⚠️ No profile found for this wallet address');
    }
    
    // Delete wallet-to-username mapping from Firestore (even if it doesn't exist)
    try {
      const userMapRef = doc(db, 'walletUsers', normalizedAddress);
      console.log('🗑️ Deleting wallet mapping:', normalizedAddress);
      await deleteDoc(userMapRef);
      console.log('✅ Wallet mapping deleted:', normalizedAddress);
    } catch (walletMapError) {
      // Don't throw for wallet mapping errors - it might already be deleted
      console.warn('⚠️ Could not delete wallet mapping:', walletMapError.code, walletMapError.message);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting user profile:', error);
    throw error;
  }
};

/**
 * Get all profiles from Firestore
 * @returns {Promise<Array>} Array of profile data
 */
export const getAllProfiles = async () => {
  try {
    // Load production profiles
    const prodCollection = collection(db, 'profiles');
    const prodSnapshot = await getDocs(prodCollection);

    const profiles = [];
    prodSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      profiles.push({
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        _isTestRecord: data._isTestRecord === true ? true : false
      });
    });

    // Load test profiles
    const testCollection = collection(db, 'profile_test');
    const testSnapshot = await getDocs(testCollection);
    testSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      profiles.push({
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        _isTestRecord: true
      });
    });

    console.log('Loaded profiles from Firestore (prod + test):', profiles.length);
    return profiles;
  } catch (error) {
    console.error('Error loading all profiles:', error);
    return [];
  }
};

/**
 * Clear all profiles and wallet mappings from Firestore, except the current user's
 * @param {string} excludeWalletAddress - Wallet address to exclude from deletion
 * @returns {Promise<boolean>}
 */
export const clearAllProfiles = async (excludeWalletAddress = null) => {
  try {
    let currentUsername = null;
    
    // If excluding a wallet, get the username for that wallet
    if (excludeWalletAddress) {
      currentUsername = await getUsernameFromWallet(excludeWalletAddress);
    }
    
    // Get all profiles
    const profilesCollection = collection(db, 'profiles');
    const profilesSnapshot = await getDocs(profilesCollection);
    
    // Delete all profiles except current user's
    const deleteProfilePromises = [];
    profilesSnapshot.forEach((doc) => {
      // Skip current user's profile
      if (currentUsername && doc.id === currentUsername) {
        console.log('Skipping current user profile:', doc.id);
        return;
      }
      deleteProfilePromises.push(deleteDoc(doc.ref));
    });
    await Promise.all(deleteProfilePromises);
    console.log(`Deleted ${deleteProfilePromises.length} profiles`);
    
    // Get all wallet mappings
    const walletUsersCollection = collection(db, 'walletUsers');
    const walletUsersSnapshot = await getDocs(walletUsersCollection);
    
    // Delete all wallet mappings except current user's
    const deleteWalletPromises = [];
    const normalizedExcludeAddress = excludeWalletAddress ? excludeWalletAddress.toLowerCase() : null;
    walletUsersSnapshot.forEach((doc) => {
      // Skip current user's wallet mapping
      if (normalizedExcludeAddress && doc.id === normalizedExcludeAddress) {
        console.log('Skipping current user wallet mapping:', doc.id);
        return;
      }
      deleteWalletPromises.push(deleteDoc(doc.ref));
    });
    await Promise.all(deleteWalletPromises);
    console.log(`Deleted ${deleteWalletPromises.length} wallet mappings`);
    
    return true;
  } catch (error) {
    console.error('Error clearing all profiles:', error);
    throw error;
  }
};

/**
 * Delete all test profiles from Firestore ('profile_test' collection)
 * Admin-only action should be enforced by caller/UI
 */
export const clearAllTestProfiles = async () => {
  try {
    const profilesCollection = collection(db, 'profile_test');
    const profilesSnapshot = await getDocs(profilesCollection);

    const deletePromises = [];
    profilesSnapshot.forEach((docSnap) => {
      deletePromises.push(deleteDoc(docSnap.ref));
    });
    await Promise.all(deletePromises);
    console.log(`Deleted ${deletePromises.length} test profiles from 'profile_test'`);
    return true;
  } catch (error) {
    console.error('Error clearing test profiles:', error);
    throw error;
  }
};

